import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} GiftCardRecipientRefs
 * @property {HTMLInputElement} recipientName
 * @property {HTMLInputElement} recipientEmail
 * @property {HTMLInputElement} fromName
 * @property {HTMLTextAreaElement} message - Visible message field. Unnamed; mirrored into messageValue.
 * @property {HTMLInputElement} messageValue - Hidden `properties[Message]`, carries message + sender.
 * @property {HTMLInputElement} sendOn
 * @property {HTMLDivElement} dateWrapper
 * @property {HTMLInputElement} [timezoneOffset]
 * @property {HTMLSpanElement} [characterCount]
 * @property {HTMLDivElement} [liveRegion]
 */

/** Shopify's hard limit on a gift card message. Exceeding it returns 422 from /cart/add. */
const SHOPIFY_MESSAGE_LIMIT = 200;

/** Shopify only schedules gift card delivery up to 90 days out. */
const MAX_SEND_ON_DAYS = 90;

const ANNOUNCE_DELAY = 750;

/**
 * Maps Shopify's cart error keys onto our field refs.
 * @type {Record<string, string>}
 */
const SERVER_ERROR_FIELDS = {
  email: 'recipientEmail',
  name: 'recipientName',
  message: 'message',
  send_on: 'sendOn',
};

/** @param {Date} date @returns {string} */
function toDateValue(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Gift card recipient fields.
 *
 * Owns the character counter, the date placeholder state, the composed message value, and all
 * validation messaging. Blocking an invalid add to cart is handled by native constraint
 * validation — `AddToCartComponent.handleClick` calls `form.checkValidity()` and the browser
 * refuses to fire `submit` — so this component only has to replace the native bubble with
 * localized, styled, in-page errors.
 *
 * @extends {Component<GiftCardRecipientRefs>}
 */
class GiftCardRecipient extends Component {
  requiredRefs = ['recipientName', 'recipientEmail', 'fromName', 'message', 'messageValue', 'sendOn'];

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #announceTimer;

  /** @returns {(HTMLInputElement | HTMLTextAreaElement)[]} */
  get #fields() {
    const { recipientName, recipientEmail, fromName, message, sendOn } = this.refs;
    return [recipientName, recipientEmail, fromName, message, sendOn];
  }

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    for (const field of this.#fields) {
      // checkValidity() fires `invalid` without showing anything; the browser's interactive
      // validation fires it again and *does* show a bubble. preventDefault suppresses that so our
      // own message is the only one, and stays in the storefront's language.
      field.addEventListener(
        'invalid',
        (event) => {
          event.preventDefault();
          this.#showError(field);
        },
        { signal }
      );

      field.addEventListener(
        'input',
        () => {
          this.#clearError(field);
          // The sender feeds the composed message, so keep the hidden value current as it is typed.
          if (field === this.refs.fromName) this.#syncMessage();
        },
        { signal }
      );
      field.addEventListener(
        'blur',
        () => (field.checkValidity() ? this.#clearError(field) : this.#showError(field)),
        { signal }
      );
    }

    document.addEventListener(ThemeEvents.cartError, this.#handleCartError, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });

    this.#setDateConstraints();
    this.#enableTimezoneOffset();
    this.#syncMessage();
    this.#updateCharacterCount();
    this.#syncDateState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    clearTimeout(this.#announceTimer);
  }

  handleMessageInput() {
    this.#syncMessage();
    this.#updateCharacterCount();
  }

  handleDateInput() {
    this.#syncDateState();
  }

  /**
   * Mirrors the visible message into the hidden `properties[Message]`, appending the sender so it
   * reaches the recipient — Shopify's gift card email has no sender field.
   */
  #syncMessage() {
    const { message, messageValue, fromName } = this.refs;
    const raw = message.value;
    const sender = fromName.value.trim();

    if (this.dataset.appendSender !== 'true' || sender === '') {
      messageValue.value = raw;
      return;
    }

    const prefix = this.dataset.senderPrefix || '—';
    const suffix = raw === '' ? `${prefix} ${sender}` : `\n\n${prefix} ${sender}`;

    // The field maxlengths are budgeted to stay under the limit; truncate anyway so a settings
    // change can never turn this into a 422.
    messageValue.value = `${raw}${suffix}`.slice(0, SHOPIFY_MESSAGE_LIMIT);
  }

  #updateCharacterCount() {
    const { characterCount, message } = this.refs;
    if (!characterCount) return;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const used = message.value.length;
    const max = message.maxLength;

    const fill = (/** @type {string} */ str) =>
      str.replace('[current]', String(used)).replace('[max]', String(max));

    characterCount.textContent = fill(template);

    const announceTemplate = characterCount.getAttribute('data-announce-template') || template;

    // The visible counter is referenced by aria-describedby, so it is read on focus rather than on
    // every keystroke. Announce separately, debounced, and immediately at the limit.
    clearTimeout(this.#announceTimer);
    if (used >= max) {
      this.#announce(this.dataset.limitReached || '');
    } else {
      this.#announceTimer = setTimeout(() => this.#announce(fill(announceTemplate)), ANNOUNCE_DELAY);
    }
  }

  #syncDateState() {
    const { dateWrapper, sendOn } = this.refs;
    if (!dateWrapper) return;

    dateWrapper.dataset.empty = sendOn.value === '' ? 'true' : 'false';
  }

  #setDateConstraints() {
    const { sendOn } = this.refs;
    const today = new Date();
    const max = new Date();
    max.setDate(today.getDate() + MAX_SEND_ON_DAYS);

    sendOn.setAttribute('min', toDateValue(today));
    sendOn.setAttribute('max', toDateValue(max));
  }

  /**
   * The offset ships disabled so that if this script never runs, Shopify falls back to interpreting
   * `Send on` in shop time rather than receiving an empty value.
   */
  #enableTimezoneOffset() {
    const { timezoneOffset } = this.refs;
    if (!timezoneOffset) return;

    timezoneOffset.value = String(new Date().getTimezoneOffset());
    timezoneOffset.disabled = false;
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @returns {string}
   */
  #messageFor(field) {
    const { validity } = field;

    if (validity.valueMissing) return this.dataset.errorRequired || '';
    if (validity.typeMismatch || validity.patternMismatch) {
      return field.type === 'email' ? this.dataset.errorEmail || '' : this.dataset.errorRequired || '';
    }
    if (validity.rangeUnderflow || validity.rangeOverflow) return this.dataset.errorDate || '';

    return field.validationMessage;
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @param {string} [message]
   */
  #showError(field, message) {
    const slot = document.getElementById(`${field.id}-error`);
    const text = message ?? this.#messageFor(field);

    field.setAttribute('aria-invalid', 'true');

    if (!slot) return;

    // The icon span is first; the message span is last.
    const target = slot.querySelector('span:last-of-type');
    if (target) target.textContent = text;

    slot.classList.remove('hidden');

    // Append rather than replace: the message field already points at its character counter,
    // and overwriting would drop that from the accessible description.
    const described = (field.dataset.describedby ?? field.getAttribute('aria-describedby') ?? '')
      .split(' ')
      .filter(Boolean);
    field.dataset.describedby = described.join(' ');
    if (!described.includes(slot.id)) described.push(slot.id);
    field.setAttribute('aria-describedby', described.join(' '));
  }

  /** @param {HTMLInputElement | HTMLTextAreaElement} field */
  #clearError(field) {
    const slot = document.getElementById(`${field.id}-error`);

    field.removeAttribute('aria-invalid');

    // Restore the original description (the counter on the message field) instead of dropping
    // aria-describedby entirely.
    const original = field.dataset.describedby ?? '';
    if (original) field.setAttribute('aria-describedby', original);
    else field.removeAttribute('aria-describedby');

    if (!slot) return;

    const target = slot.querySelector('span:last-of-type');
    if (target) target.textContent = '';
    slot.classList.add('hidden');
  }

  #clearAllErrors() {
    for (const field of this.#fields) this.#clearError(field);
  }

  /**
   * Surfaces Shopify's own validation. The cart response's `message` is only ever the string
   * "Cart Error"; the useful detail is in `errors`, keyed email / name / message / send_on.
   *
   * @param {Event} event
   */
  #handleCartError = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    const errors = detail?.data?.errors;
    if (!errors || typeof errors !== 'object') return;

    let count = 0;

    for (const [key, value] of Object.entries(errors)) {
      const refName = SERVER_ERROR_FIELDS[key];
      const field = refName ? this.refs[refName] : null;
      if (!field) continue;

      this.#showError(field, Array.isArray(value) ? value.join(', ') : String(value));
      count += 1;
    }

    if (count > 0) this.#announceCount(count);
  };

  #handleCartUpdate = () => {
    this.#clearAllErrors();
  };

  /** @param {number} count */
  #announceCount(count) {
    const template = count === 1 ? this.dataset.errorSummaryOne : this.dataset.errorSummaryOther;
    this.#announce((template || '').replace('[count]', String(count)));
  }

  /** @param {string} text */
  #announce(text) {
    const { liveRegion } = this.refs;
    if (liveRegion) liveRegion.textContent = text;
  }
}

if (!customElements.get('gift-card-recipient-component')) {
  customElements.define('gift-card-recipient-component', GiftCardRecipient);
}
